import { AxiosResponse } from "axios";
import { useState, useCallback, useEffect, useRef } from "react";

type ApiFunction<T> = (...args: any[]) => Promise<AxiosResponse<T>>;

interface ApiOptions {
  autoFetch?: boolean;
  args?: any[];
}

export function useApi<T>(apiFunc: ApiFunction<T>, options: ApiOptions = {}) {
  const { autoFetch = true, args = [] } = options;
  const argsRef = useRef(args);

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const execute = useCallback(
    async (...callArgs: any[]) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const res = await apiFunc(
          ...callArgs.concat({ signal: controller.signal })
        );

        setData(res.data);
      } catch (err: any) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
          console.log("Request cancelled");
          setData(null);
        } else {
          setError(err?.message ?? "Unknown error");
          setData(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [apiFunc]
  );

  useEffect(() => {
    if (autoFetch) {
      execute(...argsRef.current);
    }

    return () => {
      // cancel request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [execute, autoFetch]);

  return {
    data,
    loading,
    error,
    execute,
    cancel: () => abortControllerRef.current?.abort(),
  };
}
